/**
 * Mulberry Extended Warranty integration.
 * Logic from theme 153495044196. Inline form as theme block in main-product.
 *
 * CUSTOM OVERRIDE: Form submit handler for add-to-cart; reliable form resolution.
 * Restored automatically by scripts/theme-pull-safe.sh after theme pull.
 */
let offers;
let product;
let theVariant;
let selectedVariant;
window.selectedOffer = null;
let mulberryPdpInitialized = false;
let mulberryInitPromise;
let mulberryPrefetchPromise;
let pdpUrlListenersBound = false;
let variantPickerInitBound = false;
const mulberryPerfEnabled = false;
let mulberryLoadedResolve;
const mulberryLoadedPromise = new Promise((resolve) => {
  mulberryLoadedResolve = resolve;
});

// Helper function to get app-provider and update cart with new data
async function updateCartAndDispatch() {
  try {
    const appProvider = document.querySelector("app-provider");
    if (!appProvider) {
      console.warn("app-provider element not found");
      return;
    }

    // Fetch latest cart data
    const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`, {
      credentials: "same-origin",
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    });
    const cartData = await cartResponse.json();

    // Step 1: Dispatch cart:updated event on app-provider
    appProvider.dispatchEvent(new CustomEvent("cart:updated", {
      detail: cartData,
      bubbles: true,
      composed: true,
    }));

    // Step 2: Fetch and dispatch cart sections
    const sectionsResponse = await fetch(
      `${window.location.pathname}?sections=cart-drawer`
    );
    const sectionsData = await sectionsResponse.json();

    appProvider.dispatchEvent(new CustomEvent("cart:updated-sections", {
      detail: sectionsData,
      bubbles: true,
      composed: true,
    }));

    return cartData;
  } catch (error) {
    console.error("Error updating cart:", error);
  }
}

// Helper function to open cart drawer
function openCartDrawer() {
  const appProvider = document.querySelector("app-provider");
  if (!appProvider) {
    console.warn("app-provider element not found");
    return;
  }

  // Step 3: Open drawer after cart is updated
  appProvider.dispatchEvent(new CustomEvent("modal-dialog::open", {
    detail: "cart-drawer-dialog",
    bubbles: true,
    composed: true,
  }));
}

function runWhenIdle(callback) {
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(callback);
    return;
  }
  window.setTimeout(callback, 200);
}

function markMulberryPerf(label) {
  if (!mulberryPerfEnabled || !window.performance?.mark) {
    return;
  }
  window.performance.mark(label);
}

function isPdpContext() {
  return (
    !!document.querySelector("#pdp-atc") ||
    !!document.querySelector(".mulberry-pdp-inline")
  );
}

function getProductQuantity() {
  const qtyInput = document.querySelector("#productQuantity") || document.querySelector("main-product input[name='quantity']");
  return qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
}

function syncVariantFromForm() {
  const productForm = document.querySelector("main-product product-form, product-form");
  const variantId = productForm?.getAttribute?.("variant-id");
  const form = getPdpForm();
  const hiddenId = form?.querySelector?.("input[name='id']")?.value;
  const id = variantId || hiddenId;
  if (id && window.mulberryShop) {
    mulberryShop.selectedVariantId = id;
  }
}

async function ensureMulberryPdpReady() {
  if (!isPdpContext()) {
    return false;
  }
  if (mulberryPdpInitialized) {
    return true;
  }
  markMulberryPerf("mulberry:loaded:wait:start");
  await mulberryLoadedPromise;
  markMulberryPerf("mulberry:loaded:wait:end");
  if (mulberryPdpInitialized) {
    return true;
  }
  if (mulberryInitPromise) {
    return mulberryInitPromise;
  }
  mulberryInitPromise = (async () => {
    if (!window.mulberry || !window.mulberryShop) {
      return false;
    }
    await mulberryPdpInit();
    mulberryPdpInitialized = true;
    return true;
  })();
  return mulberryInitPromise;
}

function schedulePdpInit({ eager = false } = {}) {
  const inlineContainer = document.querySelector(".mulberry-pdp-inline");
  markMulberryPerf("mulberry:init:schedule");
  prefetchMulberryOffers();
  if (eager) {
    ensureMulberryPdpReady();
    return;
  }
  if (!inlineContainer) {
    runWhenIdle(() => {
      ensureMulberryPdpReady();
    });
    return;
  }

  if ("IntersectionObserver" in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          ensureMulberryPdpReady();
        }
      },
      { rootMargin: "200px 0px" }
    );
    observer.observe(inlineContainer);
    return;
  }

  runWhenIdle(() => {
    ensureMulberryPdpReady();
  });
}

function getCurrentlySelectedVariant(product) {
  let variant = null;
  if (product) {
    variant = product.variants.find((i) => i.id == mulberryShop.selectedVariantId);
    if (!variant) {
      variant = product.variants[0];
    }
    mulberryShop.selectedVariantId = variant.id;
  }
  return variant;
}

function getPriceForMulberry(variant) {
  const price = variant.compare_at_price != null
    ? variant.compare_at_price
    : variant.price != null
      ? variant.price
      : 0;
  const num = typeof price === "number" ? price / 100 : parseFloat(price) || 0;
  return num.toFixed(2);
}

async function mulberryPdpInit() {
  if (mulberryPdpInitialized) {
    return;
  }
  markMulberryPerf("mulberry:init:start");

  const HAS_MODAL = mulberry?.core?.settings?.has_modal ?? true;
  const HAS_INLINE = mulberry?.core?.settings?.has_inline ?? true;

  await prefetchMulberryOffers();
  if (!product || !offers) {
    return;
  }

  if (HAS_INLINE && document.querySelector(".mulberry-pdp-inline")) {
    markMulberryPerf("mulberry:inline:init:start");
    window.pdpInline = await mulberry.inline.init({
      offers: offers,
      settings: mulberry.core.settings,
      selector: ".mulberry-pdp-inline",
      onWarrantyToggle: async (warranty) => {
        mulberry.inline.isSelected = warranty.isSelected;
        if (warranty.isSelected) {
          window.selectedOffer = warranty;
        } else {
          window.selectedOffer = null;
        }
      },
    });
    markMulberryPerf("mulberry:inline:init:end");
    if (HAS_INLINE && offers?.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("mulberry_test") === "1") {
        window.selectedOffer = offers[0];
      }
    }
  }

  if (HAS_MODAL) {
    markMulberryPerf("mulberry:modal:init:start");
    window.pdpModal = await mulberry.modal.init({
      offers: offers,
      settings: mulberry.core.settings,
      onWarrantySelect: async (warranty) => {
        try {
          syncVariantFromForm();
          const result = await mulberryShop.getWarrantyVariant(warranty);
          await mulberryShop.addWarrantyToCart(
            result.shopify_variant_id,
            {
              ...product,
              title: product.title,
              variantId: mulberryShop.selectedVariantId,
              quantity: getProductQuantity(),
            },
            warranty
          );
          // Submit form to add the main product as well
          const f = getPdpForm();
          if (f) {
            window._mulberryAllowSubmit = true;
            f.requestSubmit();
            delete window._mulberryAllowSubmit;
          }
          // Update cart and drawer after both items are added
          await updateCartAndDispatch();
          openCartDrawer();
        } catch (err) {
          console.warn("Mulberry addWarrantyToCart failed, falling back to product-only add:", err);
          const f = getPdpForm();
          if (f) {
            window._mulberryAllowSubmit = true;
            f.requestSubmit();
            delete window._mulberryAllowSubmit;
          }
          await updateCartAndDispatch();
          openCartDrawer();
        }
        mulberry.modal.close();
        document?.querySelector("#pdp-atc")?.removeAttribute("disabled");
      },
      onWarrantyDecline: async () => {
        const f = getPdpForm();
        if (f) {
          window._mulberryAllowSubmit = true;
          f.requestSubmit();
          delete window._mulberryAllowSubmit;
        }
        mulberry.modal.close();
        document?.querySelector("#pdp-atc")?.removeAttribute("disabled");
        await updateCartAndDispatch();
        openCartDrawer();
      },
    });
    markMulberryPerf("mulberry:modal:init:end");
  }

  document.addEventListener("variant:change", async function (e) {
    if (!e.detail?.variant) return;
    window.selectedOffer = null;
    if (window.pdpModal) {
      window.pdpModal.updateOffer([]);
    }
    selectedVariant = e.detail.variant;
    if (!mulberry?.core?.getWarrantyOffer) return;
    offers = await mulberry.core.getWarrantyOffer({
      id: selectedVariant.id,
      title: product.title,
      price: getPriceForMulberry(selectedVariant),
      detail: product,
    });
    if (window.pdpInline) {
      window.pdpInline.updateOffer(offers);
    }
    if (window.pdpModal) {
      window.pdpModal.updateOffer(offers);
    }
  });

  document.addEventListener("variant:changed", async function (e) {
    if (!e.detail?.variant) return;
    window.selectedOffer = null;
    if (window.pdpModal) {
      window.pdpModal.updateOffer([]);
    }
    selectedVariant = e.detail.variant;
    if (!mulberry?.core?.getWarrantyOffer) return;
    offers = await mulberry.core.getWarrantyOffer({
      id: selectedVariant.id,
      title: product.title,
      price: getPriceForMulberry(selectedVariant),
      detail: product,
    });
    if (window.pdpInline) {
      window.pdpInline.updateOffer(offers);
    }
    if (window.pdpModal) {
      window.pdpModal.updateOffer(offers);
    }
  });

  setupPdpUrlListeners();
  markMulberryPerf("mulberry:init:end");
}

function getPdpForm() {
  const mainBtn = document.querySelector("#pdp-atc");
  if (mainBtn?.form) return mainBtn.form;
  const stickyBtn = document.querySelector("[data-main-button-id='pdp-atc']");
  if (stickyBtn?.dataset?.formId) {
    const f = document.getElementById(stickyBtn.dataset.formId);
    if (f) return f;
  }
  return document.querySelector("main-product form[data-type='add-to-cart-form'], main-product-bundle form[data-type='add-to-cart-form']");
}

function setupPdpAtcHandler() {
  document.addEventListener(
    "submit",
    async function (ev) {
      const form = ev.target;
      if (!form || typeof form.tagName !== "string" || form.tagName.toLowerCase() !== "form") return;
      if (form.getAttribute("data-type") !== "add-to-cart-form") return;
      if (!form.closest("main-product") && !form.closest("main-product-bundle")) return;
      const submitBtn = form.querySelector("#pdp-atc");
      if (!submitBtn) return;
      if (window._mulberryAllowSubmit) return;

      const isReady = await ensureMulberryPdpReady();
      if (!isReady) return;

      const HAS_MODAL = mulberry?.core?.settings?.has_modal ?? true;
      const HAS_INLINE = mulberry?.core?.settings?.has_inline ?? true;

      if (HAS_INLINE && window.selectedOffer) {
        ev.preventDefault();
        ev.stopPropagation();
        submitBtn.setAttribute("disabled", "");
        document.querySelector("[data-main-button-id='pdp-atc']")?.setAttribute("disabled", "");
        await addWarranty(form);
      } else if (HAS_MODAL && window.pdpModal && !window.selectedOffer) {
        ev.preventDefault();
        ev.stopPropagation();
        submitBtn.setAttribute("disabled", "");
        document.querySelector("[data-main-button-id='pdp-atc']")?.setAttribute("disabled", "");
        syncVariantFromForm();
        window.pdpModal.open();
      }
    },
    { capture: true }
  );
}
setupPdpAtcHandler();

function resolveOfferFromSelection(selected) {
  if (!selected) return null;
  const offer = selected?.offer ?? selected?.warranty ?? selected;
  if (offer?.id || offer?.warranty_hash) return offer;
  if (offers?.length && (offer?.duration_months || offer?.customer_cost)) {
    const match = offers.find(
      (o) =>
        (offer.id && o.id === offer.id) ||
        (offer.warranty_hash && o.warranty_hash === offer.warranty_hash) ||
        (offer.duration_months && String(o.duration_months) === String(offer.duration_months) && offer.customer_cost === o.customer_cost)
    );
    if (match) return match;
  }
  return offer;
}

async function addWarranty(form) {
  form = form || getPdpForm();
  const fallbackAddProduct = async () => {
    document?.querySelector("#pdp-atc")?.removeAttribute("disabled");
    document?.querySelector("[data-main-button-id='pdp-atc']")?.removeAttribute("disabled");
    window.selectedOffer = null;
    if (form) {
      window._mulberryAllowSubmit = true;
      form.requestSubmit();
      delete window._mulberryAllowSubmit;
    }
    await updateCartAndDispatch();
    openCartDrawer();
  };

  if (!offers || offers.length === 0) {
    fallbackAddProduct();
    return;
  }
  if (!window.selectedOffer) {
    fallbackAddProduct();
    return;
  }

  try {
    syncVariantFromForm();
    const offer = resolveOfferFromSelection(window.selectedOffer) ?? window.selectedOffer;
    if (!offer || (!offer.id && !offer.warranty_hash)) {
      fallbackAddProduct();
      return;
    }
    const result = await mulberryShop.getWarrantyVariant(offer);
    await mulberryShop.addWarrantyToCart(
      result.shopify_variant_id,
      {
        ...product,
        title: product.title,
        variantId: mulberryShop.selectedVariantId,
        quantity: getProductQuantity(),
      },
      offer
    );
    // Submit form to add the main product as well
    if (form) {
      window._mulberryAllowSubmit = true;
      form.requestSubmit();
      delete window._mulberryAllowSubmit;
    }
    document?.querySelector("#pdp-atc")?.removeAttribute("disabled");
    window.selectedOffer = null;
    if (window.pdpInline) {
      window.pdpInline.deselectOffer();
    }
    // Update cart with fresh data, then open drawer
    await updateCartAndDispatch();
    openCartDrawer();
  } catch (err) {
    console.warn("Mulberry addWarrantyToCart failed, falling back to product-only add:", err);
    fallbackAddProduct();
  }
}

document.addEventListener("mulberry-shopify:loaded", async function () {
  window.mb_gtm_is_disabled = true;
  setupFetchListeners();
  if (mulberryLoadedResolve) {
    mulberryLoadedResolve();
  }

  if (isPdpContext()) {
    schedulePdpInit({ eager: true });
    bindVariantPickerInit();
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => {
        schedulePdpInit({ eager: true });
      });
    }
  }

  runWhenIdle(() => {
    removeAndSyncWarranties().catch((error) => {
      console.error("Error during warranty removal and cart sync:", error);
    });

    if (window.mulberryShop?.cart?.init) {
      mulberryShop.cart
        .init()
        .then(() => mulberryShop.cart.render())
        .catch((error) => {
          console.error("Error during cart init/render:", error);
        });
    }
  });
});

if (window.mulberry && window.mulberryShop && mulberryLoadedResolve) {
  mulberryLoadedResolve();
}

async function removeAndSyncWarranties() {
  const response = await fetch("/cart.js", {
    credentials: "same-origin",
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
  });

  let firstCart = await response.json();
  firstCart.items = firstCart.items.filter(
    (item) => item?.properties?._variantId !== "sectional"
  );

  try {
    await mulberryShop.removeDetachedWarranty(firstCart);
    await mulberryShop.cart.syncQuantities(firstCart);
  } catch (error) {
    console.error("Error during warranty removal and cart sync:", error);
  }
}

async function prefetchMulberryOffers() {
  if (!isPdpContext()) return null;
  if (offers) return offers;
  if (mulberryPrefetchPromise) return mulberryPrefetchPromise;

  mulberryPrefetchPromise = (async () => {
    await mulberryLoadedPromise;
    if (!window.mulberry || !window.mulberryShop || !mulberry?.core?.getWarrantyOffer) return null;
    markMulberryPerf("mulberry:offers:fetch:start");
    const response = await mulberryShop.getProductData(mulberryShop.getProductUrl());
    product = response.product;
    const initSelection = getCurrentlySelectedVariant(product);
    if (initSelection) {
      theVariant = initSelection.id;
    }
    selectedVariant = getCurrentlySelectedVariant(product);
    const price = getPriceForMulberry(selectedVariant);
    offers = await mulberry.core.getWarrantyOffer({
      id: selectedVariant.id,
      title: product.title,
      price: price,
      detail: product,
    });
    markMulberryPerf("mulberry:offers:fetch:end");
    return offers;
  })();

  return mulberryPrefetchPromise;
}

function setupPdpUrlListeners() {
  if (pdpUrlListenersBound) return;
  pdpUrlListenersBound = true;

  function getUrlParameter(name) {
    const regex = new RegExp("[\\?&]" + name.replace(/[[\]]/g, "\\$&") + "=([^&#]*)");
    const results = regex.exec(location.search);
    return results === null ? "" : decodeURIComponent(results[1].replace(/\+/g, " "));
  }

  async function handleUrlChange() {
    const variant = getUrlParameter("variant");
    if (variant && theVariant !== variant && product) {
      theVariant = variant;
      window.selectedOffer = null;
      mulberryShop.selectedVariantId = variant;
      selectedVariant = getCurrentlySelectedVariant(product);

      if (selectedVariant && mulberry.core?.getWarrantyOffer) {
        offers = await mulberry.core.getWarrantyOffer({
          id: selectedVariant.id,
          title: product.title,
          price: getPriceForMulberry(selectedVariant),
          detail: product,
        });
        if (window.pdpInline) window.pdpInline.updateOffer(offers);
        if (window.pdpModal) window.pdpModal.updateOffer(offers);
      }
    }
  }

  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.type === "attributes" && mutation.attributeName === "href") {
        handleUrlChange();
      }
    });
  });

  observer.observe(document.body, {
    attributes: true,
    subtree: true,
    attributeFilter: ["href"],
  });

  ["click", "touchstart"].forEach(function (evt) {
    document.addEventListener(
      evt,
      function (e) {
        if (
          e.target.closest(".radio-group__color") ||
          e.target.closest(".option-groups__group") ||
          e.target.closest("[data-variant-id]")
        ) {
          setTimeout(handleUrlChange, 100);
        }
      },
      { passive: true }
    );
  });

  handleUrlChange();
}

function bindVariantPickerInit() {
  if (variantPickerInitBound) return;
  variantPickerInitBound = true;

  const eagerInit = () => schedulePdpInit({ eager: true });

  document.addEventListener("variantPicker:ready:handle", eagerInit);
  document.addEventListener("changeVariant", eagerInit);
}

function setupFetchListeners() {
  const { fetch: origFetch } = window;
  window.fetch = async function (...args) {
    const response = await origFetch.apply(this, args);
    let requestUrl = args[0];
    if (typeof requestUrl === "string" && requestUrl.includes("cart/change")) {
      response
        .clone()
        .json()
        .then(async () => {
          await removeAndSyncWarranties();
          await updateCartAndDispatch();
        })
        .catch((err) => console.error(err));
    }
    return response;
  };
}
